import React, { Component } from "react";
import { Modal, Table, Tag, Button, Row, Col, Typography, Input, Select, Form, message } from "antd";

const { Text, Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

export default class CardOrderDetails extends Component {
  constructor(props) {
    super(props);
    this.state = {
      revealedPins: {},
      resolveModalVisible: false,
      resolvingUnit: null,
      resolutionType: "refunded",
      auditNote: "",
      evidence: "",
      voucherCode: "",
      serialNumber: "",
      actualCost: "",
      currency: "LYD",
      isSubmitting: false,
    };
  }

  toggleRevealPin = (unitId) => {
    this.setState((prev) => ({
      revealedPins: {
        ...prev.revealedPins,
        [unitId]: !prev.revealedPins[unitId]
      }
    }));
  };

  openResolveModal = (unit) => {
    const cost = unit.pricing_snapshot ? unit.pricing_snapshot.supplierCost : "";
    const curr = unit.pricing_snapshot ? unit.pricing_snapshot.supplierCurrency : "LYD";
    this.setState({
      resolveModalVisible: true,
      resolvingUnit: unit,
      resolutionType: "refunded",
      auditNote: "",
      evidence: "",
      voucherCode: "",
      serialNumber: "",
      actualCost: cost,
      currency: curr,
    });
  };

  closeResolveModal = () => {
    this.setState({
      resolveModalVisible: false,
      resolvingUnit: null,
    });
  };

  handleConfirmResolve = async () => {
    const { resolvingUnit, resolutionType, auditNote, evidence, voucherCode, serialNumber, actualCost, currency } = this.state;
    if (!resolvingUnit) return;

    if (!auditNote || auditNote.trim().length < 3) {
      message.error("Audit note is required (minimum 3 characters)");
      return;
    }
    if (!evidence || !evidence.trim()) {
      message.error("Evidence reference is required");
      return;
    }

    if (resolutionType === "completed" && (!voucherCode || !voucherCode.trim())) {
      message.error("Voucher code is required for completion");
      return;
    }

    const payload = {
      resolution: resolutionType,
      audit_note: auditNote.trim(),
      evidence: evidence.trim(),
      ...(resolutionType === "completed" ? {
        code: voucherCode.trim(),
        serial: serialNumber ? serialNumber.trim() : null,
        actual_cost: actualCost || "0.000",
        currency: currency || "LYD"
      } : {})
    };

    try {
      this.setState({ isSubmitting: true });
      if (typeof this.props.onResolveUnit === "function") {
        await this.props.onResolveUnit(resolvingUnit.id, payload);
      }
      this.closeResolveModal();
    } catch (err) {
      message.error(err.message || "Failed to resolve unit");
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  renderUnitStateTag = (state) => {
    const colors = {
      COMPLETED: "green",
      REFUNDED: "orange",
      NEEDS_REVIEW: "volcano",
      PURCHASING: "processing",
      PURCHASED: "cyan",
      DEBITED: "blue",
      QUOTED: "default",
    };
    return <Tag color={colors[state] || "default"}>{state}</Tag>;
  };

  render() {
    const { isVisible, order, onClose, onRecheckUnit } = this.props;
    if (!order) return null;

    const units = order.units || [];
    const events = order.events || [];
    const totals = order.totals_snapshot || {};

    const unitColumns = [
      {
        title: "Unit ID",
        dataIndex: "id",
        key: "id",
        render: (id) => <Text code>{id ? id.substring(0, 8) + "..." : "N/A"}</Text>,
      },
      {
        title: "Card ID",
        dataIndex: "card_id",
        key: "card_id",
        render: (cid) => <Text>{cid ? cid.substring(0, 8) + "..." : "N/A"}</Text>,
      },
      {
        title: "State",
        dataIndex: "state",
        key: "state",
        render: (st) => this.renderUnitStateTag(st),
      },
      {
        title: "Sale Price",
        key: "sale",
        render: (_, u) => (u.pricing_snapshot ? `${u.pricing_snapshot.sale} LYD` : "N/A"),
      },
      {
        title: "Supplier Cost",
        key: "cost",
        render: (_, u) => (u.pricing_snapshot ? `${u.pricing_snapshot.supplierCost} ${u.pricing_snapshot.supplierCurrency}` : "N/A"),
      },
      {
        title: "Voucher PIN",
        key: "pin",
        render: (_, u) => {
          const isRevealed = this.state.revealedPins[u.id];
          const hasVoucher = Boolean(u.redeem_code);
          if (!hasVoucher) return <Text type="secondary">Not delivered</Text>;
          return (
            <div>
              {isRevealed ? (
                <Text copyable code strong>{u.redeem_code}</Text>
              ) : (
                <Button size="small" onClick={() => this.toggleRevealPin(u.id)}>
                  Reveal PIN
                </Button>
              )}
            </div>
          );
        },
      },
      {
        title: "Actions",
        key: "actions",
        render: (_, u) => {
          const isTerminal = ["COMPLETED", "REFUNDED"].includes(u.state);
          return (
            <div style={{ display: "flex", gap: 8 }}>
              <Button
                size="small"
                disabled={isTerminal}
                onClick={() => onRecheckUnit && onRecheckUnit(u.id)}
              >
                Recheck
              </Button>
              <Button
                size="small"
                type="primary"
                danger={u.state === "NEEDS_REVIEW"}
                disabled={isTerminal}
                onClick={() => this.openResolveModal(u)}
              >
                Resolve
              </Button>
            </div>
          );
        },
      },
    ];

    const eventColumns = [
      {
        title: "Timestamp",
        dataIndex: "created_at",
        key: "created_at",
        render: (ts) => (ts ? new Date(ts).toLocaleString() : "N/A"),
      },
      {
        title: "Actor",
        key: "actor",
        render: (_, ev) => `${ev.actor_type || "system"} (${ev.actor_id || "N/A"})`,
      },
      {
        title: "Transition",
        key: "transition",
        render: (_, ev) => `${ev.from_state || "*"} → ${ev.to_state || "*"}`,
      },
      {
        title: "Operation",
        dataIndex: "operation",
        key: "operation",
      },
      {
        title: "Safe Code",
        dataIndex: "safe_code",
        key: "safe_code",
        render: (sc) => (sc ? <Tag>{sc}</Tag> : "-"),
      },
      {
        title: "Note / Evidence",
        key: "note",
        render: (_, ev) => (
          <div>
            {ev.audit_note && <div>{ev.audit_note}</div>}
            {ev.evidence_reference && <Text type="secondary">Ref: {ev.evidence_reference}</Text>}
          </div>
        ),
      },
    ];

    return (
      <Modal
        title={`Order Details: ${order.id}`}
        open={isVisible}
        onCancel={onClose}
        width={950}
        footer={[
          <Button key="close" onClick={onClose}>
            Close
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 20 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Text type="secondary">Status:</Text>
              <div>{this.renderUnitStateTag(order.state)}</div>
            </Col>
            <Col span={6}>
              <Text type="secondary">User ID:</Text>
              <div><Text strong>{order.user_id}</Text></div>
            </Col>
            <Col span={6}>
              <Text type="secondary">Total Sale:</Text>
              <div><Text strong>{totals.sale || "0.000"} LYD</Text></div>
            </Col>
            <Col span={6}>
              <Text type="secondary">Units (Done/Pend/Ref):</Text>
              <div>
                <Text strong>
                  {totals.completed_units || 0} / {totals.pending_units || 0} / {totals.refunded_units || 0}
                </Text>
              </div>
            </Col>
          </Row>
        </div>

        <Title level={5}>Order Units</Title>
        <Table
          columns={unitColumns}
          dataSource={units}
          rowKey="id"
          pagination={false}
          size="small"
          style={{ marginBottom: 24 }}
        />

        <Title level={5}>Audit & Lifecycle Events</Title>
        <Table
          columns={eventColumns}
          dataSource={events}
          rowKey="id"
          pagination={{ pageSize: 5 }}
          size="small"
        />

        {/* Resolution Modal */}
        <Modal
          title={`Resolve Unit: ${this.state.resolvingUnit?.id}`}
          open={this.state.resolveModalVisible}
          onCancel={this.closeResolveModal}
          onOk={this.handleConfirmResolve}
          confirmLoading={this.state.isSubmitting}
        >
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", marginBottom: 4, fontWeight: "bold" }}>Resolution Outcome</label>
            <Select
              style={{ width: "100%" }}
              value={this.state.resolutionType}
              onChange={(val) => this.setState({ resolutionType: val })}
            >
              <Option value="refunded">Refunded (No charge / No delivery)</Option>
              <Option value="completed">Completed (Voucher delivered)</Option>
            </Select>
          </div>

          {this.state.resolutionType === "completed" && (
            <>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", marginBottom: 4, fontWeight: "bold" }}>Voucher Code (PIN)</label>
                <Input
                  placeholder="Enter delivered PIN"
                  value={this.state.voucherCode}
                  onChange={(e) => this.setState({ voucherCode: e.target.value })}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", marginBottom: 4, fontWeight: "bold" }}>Serial Number (Optional)</label>
                <Input
                  placeholder="Enter serial number"
                  value={this.state.serialNumber}
                  onChange={(e) => this.setState({ serialNumber: e.target.value })}
                />
              </div>
              <Row gutter={12} style={{ marginBottom: 12 }}>
                <Col span={14}>
                  <label style={{ display: "block", marginBottom: 4, fontWeight: "bold" }}>Actual Supplier Cost</label>
                  <Input
                    placeholder="0.000"
                    value={this.state.actualCost}
                    onChange={(e) => this.setState({ actualCost: e.target.value })}
                  />
                </Col>
                <Col span={10}>
                  <label style={{ display: "block", marginBottom: 4, fontWeight: "bold" }}>Currency</label>
                  <Select
                    style={{ width: "100%" }}
                    value={this.state.currency}
                    onChange={(val) => this.setState({ currency: val })}
                  >
                    <Option value="LYD">LYD</Option>
                    <Option value="USD">USD</Option>
                  </Select>
                </Col>
              </Row>
            </>
          )}

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", marginBottom: 4, fontWeight: "bold" }}>Evidence Reference</label>
            <Input
              placeholder="e.g. Khales support ticket # or statement reference"
              value={this.state.evidence}
              onChange={(e) => this.setState({ evidence: e.target.value })}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", marginBottom: 4, fontWeight: "bold" }}>Audit Note</label>
            <TextArea
              rows={3}
              placeholder="Explain justification for manual resolution..."
              value={this.state.auditNote}
              onChange={(e) => this.setState({ auditNote: e.target.value })}
            />
          </div>
        </Modal>
      </Modal>
    );
  }
}
